// React Yjs sync client — framework-neutral port of the Angular runtime's
// `yjs-client.service.ts`. Speaks the same minimal y-protocol framing as the
// backend gateway (`app/services/yjs_gateway.py`) and connects to the same
// `/ws/docs/{id}` socket, so React and Angular share one live-collaboration
// server with no protocol changes.
//
// The shared Y.Doc shape mirrors the backend materializer:
//   doc.getMap('blocks')  →  Y.Map<block_id, Y.Map<props>>
// i.e. one entry per document block, each a map of that block's properties.
// This is the same model React already uses for `blockProps` in
// RuntimeContext, so the eventual integration is a swap of the persistence
// layer (DocSync REST → this), not a data-model change.
//
// The wire encode/decode is split into pure functions so it can be unit
// tested by round-tripping two Y.Docs without a real WebSocket (see
// test-script/test_yjs_protocol.mjs).

import * as Y from 'yjs'
import { WS_BASE } from '@/config/basePath'

// Re-export the Y namespace so consumers (and tests) share this module's
// single Yjs instance — importing yjs twice breaks its instanceof checks
// (https://github.com/yjs/yjs/issues/438).
export { Y }

// ── Y protocol constants — must match yjs_gateway.py ──────────────────
export const MESSAGE_SYNC = 0
export const MESSAGE_AWARENESS = 1
export const SYNC_STEP_1 = 0
export const SYNC_STEP_2 = 1
export const SYNC_UPDATE = 2

// ── varint + framing (matches lib0/encoding used by the gateway) ──────

export function writeVarUint(n: number): Uint8Array {
  const out: number[] = []
  while (n > 0x7f) {
    out.push((n & 0x7f) | 0x80)
    n >>>= 7
  }
  out.push(n & 0x7f)
  return new Uint8Array(out)
}

export function readVarUint(buf: Uint8Array, offset: number): { value: number; offset: number } {
  let value = 0
  let shift = 0
  let cur = offset
  // Guard against malformed buffers running off the end.
  while (cur < buf.length) {
    const b = buf[cur++]
    value |= (b & 0x7f) << shift
    if ((b & 0x80) === 0) break
    shift += 7
  }
  return { value, offset: cur }
}

export function readVarBytes(buf: Uint8Array, offset: number): { value: Uint8Array; offset: number } {
  const len = readVarUint(buf, offset)
  const start = len.offset
  return { value: buf.subarray(start, start + len.value), offset: start + len.value }
}

export function concat(...parts: Uint8Array[]): Uint8Array {
  let total = 0
  for (const p of parts) total += p.length
  const out = new Uint8Array(total)
  let off = 0
  for (const p of parts) {
    out.set(p, off)
    off += p.length
  }
  return out
}

export function encodeMessage(typeBytes: Uint8Array, payload: Uint8Array): Uint8Array {
  return concat(typeBytes, writeVarUint(payload.length), payload)
}

/** Frame our current state vector as a SYNC_STEP_1 request. */
export function encodeSyncStep1(doc: Y.Doc): Uint8Array {
  return encodeMessage(new Uint8Array([MESSAGE_SYNC, SYNC_STEP_1]), Y.encodeStateVector(doc))
}

/** Frame a local update as a SYNC_UPDATE broadcast. */
export function encodeUpdate(update: Uint8Array): Uint8Array {
  return encodeMessage(new Uint8Array([MESSAGE_SYNC, SYNC_UPDATE]), update)
}

/**
 * Pure handler for one incoming protocol frame. Applies remote updates to
 * `doc` (with origin 'remote' so our own update fan-out doesn't echo them
 * back) and, for a peer's SYNC_STEP_1, returns the SYNC_STEP_2 reply bytes
 * to send. Returns `null` when no reply is needed.
 *
 * Kept free of any WebSocket reference so the protocol logic is unit
 * testable by piping the return value between two docs.
 */
export function handleIncoming(data: Uint8Array, doc: Y.Doc): Uint8Array | null {
  if (data.length === 0) return null
  const msgType = data[0]
  let offset = 1
  if (msgType === MESSAGE_SYNC) {
    const subType = data[offset++]
    const { value: payload } = readVarBytes(data, offset)
    if (subType === SYNC_STEP_1) {
      // Peer asked for our diff against their state vector → reply step-2.
      const update = Y.encodeStateAsUpdate(doc, payload)
      return encodeMessage(new Uint8Array([MESSAGE_SYNC, SYNC_STEP_2]), update)
    }
    if (subType === SYNC_STEP_2 || subType === SYNC_UPDATE) {
      Y.applyUpdate(doc, payload, 'remote')
      return null
    }
  }
  // AWARENESS (presence) is relayed by the server; we don't render cursors
  // yet, so it's a no-op here. Unknown types are ignored.
  return null
}

// ── Connection ────────────────────────────────────────────────────────

export interface YjsBlocksConnection {
  doc: Y.Doc
  /** Y.Map keyed by block_id → Y.Map of that block's properties. */
  blocks: Y.Map<Y.Map<unknown>>
  /** Latest connection status. */
  isConnected: () => boolean
  /** Merge a property patch onto a block (creates the block map if absent). */
  setBlockProperties: (blockId: string, props: Record<string, unknown>) => void
  /** Read a block's properties as a plain object (or undefined). */
  getBlockProperties: (blockId: string) => Record<string, unknown> | undefined
  close: () => void
}

export interface YjsConnectOpts {
  publicToken?: string
  /** Called whenever the socket connects/disconnects. */
  onStatus?: (connected: boolean) => void
  /** Called after any change to the blocks map (local or remote). */
  onBlocksChange?: () => void
  /** Reconnect backoff in ms (default 2000). */
  reconnectMs?: number
}

function buildWsUrl(documentId: string, publicToken?: string): string {
  const base = `${WS_BASE()}/ws/docs/${encodeURIComponent(documentId)}`
  return publicToken ? `${base}?link=${encodeURIComponent(publicToken)}` : base
}

/**
 * Open a live Yjs connection for `documentId`. The caller owns the
 * lifecycle and must call `close()`. Reconnects with a fixed backoff.
 */
export function connectYjsDocument(documentId: string, opts: YjsConnectOpts = {}): YjsBlocksConnection {
  const doc = new Y.Doc()
  const blocks = doc.getMap<Y.Map<unknown>>('blocks')
  const reconnectMs = opts.reconnectMs ?? 2000

  let ws: WebSocket | null = null
  let closed = false
  let connected = false

  const setConnected = (next: boolean) => {
    if (connected === next) return
    connected = next
    try { opts.onStatus?.(next) } catch { /* listener error must not kill the socket */ }
  }

  // Re-emit on any deep change to the blocks map so consumers re-render.
  if (opts.onBlocksChange) {
    blocks.observeDeep(() => {
      try { opts.onBlocksChange?.() } catch { /* ignore listener errors */ }
    })
  }

  const open = () => {
    if (closed) return
    const url = buildWsUrl(documentId, opts.publicToken)
    ws = new WebSocket(url)
    ws.binaryType = 'arraybuffer'

    ws.addEventListener('open', () => {
      setConnected(true)
      // Kick off sync: send our state vector; server replies step-2 + step-1.
      try { ws?.send(encodeSyncStep1(doc)) } catch { /* socket may have closed */ }
    })

    ws.addEventListener('message', (ev) => {
      try {
        const reply = handleIncoming(new Uint8Array(ev.data as ArrayBuffer), doc)
        if (reply && ws && ws.readyState === WebSocket.OPEN) ws.send(reply)
      } catch (err) {
        if (import.meta.env.DEV) console.warn('[yjs] failed to handle frame', err)
      }
    })

    ws.addEventListener('close', () => {
      setConnected(false)
      if (closed) return
      setTimeout(open, reconnectMs)
    })

    ws.addEventListener('error', () => {
      // 'close' fires too; reconnect logic lives there.
    })
  }

  // Fan out local updates to the server (skip ones we applied from remote).
  const updateHandler = (update: Uint8Array, origin: unknown) => {
    if (origin === 'remote') return
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    try { ws.send(encodeUpdate(update)) } catch { /* dropped; resync on reconnect */ }
  }
  doc.on('update', updateHandler)

  open()

  return {
    doc,
    blocks,
    isConnected: () => connected,
    setBlockProperties: (blockId, props) => {
      doc.transact(() => {
        let blockMap = blocks.get(blockId)
        if (!blockMap) {
          blockMap = new Y.Map()
          blocks.set(blockId, blockMap)
        }
        for (const [k, v] of Object.entries(props)) {
          blockMap.set(k, v as never)
        }
      })
    },
    getBlockProperties: (blockId) => {
      const blockMap = blocks.get(blockId)
      if (!blockMap) return undefined
      return blockMap.toJSON() as Record<string, unknown>
    },
    close: () => {
      closed = true
      doc.off('update', updateHandler)
      ws?.close()
      doc.destroy()
    },
  }
}
